import React, { FormEvent, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  GridLegacy as Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { getGiftCardBalance } from '~/services/PromoService';

import dateFormatter from '~/utils/dateFormatter';
import isProd from '~/utils/isProd';

type BalanceResult = Awaited<ReturnType<typeof getGiftCardBalance>>['result'];

const REGION_OPTIONS = ['AU', 'NZ', 'GB'];
const CODE_INPUT_PROPS = {
  autoComplete: 'off',
  style: {
    fontFamily: 'monospace',
  },
};

function isBlackhawkCardCode(code: string) {
  const trimmedCode = code.trim();
  return (trimmedCode.length === 16 || trimmedCode.length === 19) && /^\d+$/.test(trimmedCode);
}

function canBecomeBlackhawkCardCode(code: string) {
  const trimmedCode = code.trim();
  return /^\d*$/.test(trimmedCode) && trimmedCode.length <= 19;
}

function formatError(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function formatBalance(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function BalanceResultPanel({ result }: { result: BalanceResult }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">
            Remaining balance
          </Typography>
          <Typography variant="h5">{formatBalance(result.amount, result.currency)}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">
            Currency
          </Typography>
          <Typography variant="body1">{result.currency}</Typography>
        </Grid>
        {result.createdAt && (
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1">{dateFormatter(result.createdAt)}</Typography>
          </Grid>
        )}
        {result.restricted_user_ids && result.restricted_user_ids.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Restricted users
            </Typography>
            <Typography variant="body2">{result.restricted_user_ids.join(', ')}</Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

function GiftCardBalanceContainer() {
  const { enqueueSnackbar } = useSnackbar();
  const tenant = useCurrentTenant();
  const [leCode, setLeCode] = useState('');
  const [leResult, setLeResult] = useState<BalanceResult | null>(null);
  const [isLeLoading, setIsLeLoading] = useState(false);
  const [leCodeError, setLeCodeError] = useState('');
  const [leFormError, setLeFormError] = useState('');
  const [blackhawkCode, setBlackhawkCode] = useState('');
  const [blackhawkPin, setBlackhawkPin] = useState('');
  const [blackhawkRegion, setBlackhawkRegion] = useState('AU');
  const [blackhawkResult, setBlackhawkResult] = useState<BalanceResult | null>(null);
  const [isBlackhawkLoading, setIsBlackhawkLoading] = useState(false);
  const [blackhawkCodeError, setBlackhawkCodeError] = useState('');
  const [blackhawkPinError, setBlackhawkPinError] = useState('');
  const [blackhawkFormError, setBlackhawkFormError] = useState('');

  const brand = useMemo(() => tenant.tenant.brand, [tenant.tenant.brand]);
  const showBlackhawkTestInstructions = !isProd();
  const leCodeLooksLikeBlackhawk = isBlackhawkCardCode(leCode);
  const blackhawkCodeCannotBecomeBlackhawk =
    blackhawkCode.trim().length > 0 && !canBecomeBlackhawkCardCode(blackhawkCode);

  const onLeSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedCode = leCode.trim();
    let hasError = false;

    setLeCodeError('');
    setLeFormError('');

    if (!trimmedCode) {
      setLeCodeError('LE gift card code is required.');
      hasError = true;
    } else if (isBlackhawkCardCode(trimmedCode)) {
      setLeCodeError('This looks like a Blackhawk card code. Use the Blackhawk gift card form below.');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsLeLoading(true);
    setLeResult(null);

    try {
      const response = await getGiftCardBalance({
        code: trimmedCode,
        brand,
      });
      setLeResult(response.result);
      enqueueSnackbar('LE gift card balance checked', { variant: 'success' });
    } catch (error) {
      setLeFormError(`Unable to check LE gift card balance: ${formatError(error)}`);
    } finally {
      setIsLeLoading(false);
    }
  };

  const onBlackhawkSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedCode = blackhawkCode.trim();
    const trimmedPin = blackhawkPin.trim();
    let hasError = false;

    setBlackhawkCodeError('');
    setBlackhawkPinError('');
    setBlackhawkFormError('');

    if (!trimmedCode) {
      setBlackhawkCodeError('Blackhawk card code is required.');
      hasError = true;
    } else if (!canBecomeBlackhawkCardCode(trimmedCode)) {
      setBlackhawkCodeError('This cannot be a Blackhawk card code. Use the LE internal gift card form above.');
      hasError = true;
    } else if (!isBlackhawkCardCode(trimmedCode)) {
      setBlackhawkCodeError('Blackhawk card code must be 16 or 19 digits.');
      hasError = true;
    }

    if (!trimmedPin) {
      setBlackhawkPinError('Blackhawk PIN is required.');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsBlackhawkLoading(true);
    setBlackhawkResult(null);

    try {
      const response = await getGiftCardBalance({
        code: trimmedCode,
        pin: trimmedPin,
        region: blackhawkRegion,
        brand,
      });
      setBlackhawkResult(response.result);
      enqueueSnackbar('Blackhawk gift card balance checked', { variant: 'success' });
    } catch (error) {
      setBlackhawkFormError(`Unable to check Blackhawk gift card balance: ${formatError(error)}`);
    } finally {
      setIsBlackhawkLoading(false);
    }
  };

  return (
    <Container>
      <Helmet>
        <title>Gift Card Balance</title>
      </Helmet>

      <Typography variant="h4" sx={{ mb: 3 }}>
        Check Gift Card Balance
      </Typography>

      <Box>
        <Typography variant="h6" gutterBottom>
          LE internal gift card
        </Typography>
        <Box component="form" onSubmit={onLeSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                autoFocus
                label="LE Gift Card Code"
                value={leCode}
                onChange={(event) => {
                  setLeCode(event.target.value);
                  setLeCodeError('');
                  setLeFormError('');
                }}
                error={!!leCodeError || leCodeLooksLikeBlackhawk}
                helperText={
                  leCodeError ||
                  (leCodeLooksLikeBlackhawk
                    ? 'This looks like a Blackhawk card code. Use the Blackhawk gift card form below.'
                    : undefined)
                }
                inputProps={CODE_INPUT_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth type="submit" variant="contained" disabled={isLeLoading}>
                {isLeLoading ? 'Checking...' : 'Check LE Balance'}
              </Button>
            </Grid>
          </Grid>
        </Box>
        {leFormError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {leFormError}
          </Alert>
        )}
        {leResult && <BalanceResultPanel result={leResult} />}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>
          Blackhawk gift card
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This balance check cannot confirm card expiry. To confirm expiry, escalate to the gift card provider,
          BlackHawk, who produces the physical cards. Expiry is typically 3 years from date of purchase.
        </Typography>
        {showBlackhawkTestInstructions && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Test Blackhawk cards in non-production
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              <li>Use a 16 digit card number starting with 12 zeros.</li>
              <li>
                Example card code: <code>0000000000000001</code>.
              </li>
              <li>
                The PIN controls the simulated card value. Example: <code>50</code> returns a 50 dollar balance.
              </li>
              <li>
                Use region <code>AU</code> for numeric PINs.
              </li>
              <li>
                Codes ending in <code>9999</code> simulate redeem failure, but balance checks still succeed.
              </li>
            </Box>
          </Alert>
        )}
        <Box component="form" onSubmit={onBlackhawkSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Blackhawk Card Code"
                value={blackhawkCode}
                onChange={(event) => {
                  setBlackhawkCode(event.target.value);
                  setBlackhawkCodeError('');
                  setBlackhawkFormError('');
                }}
                error={!!blackhawkCodeError || blackhawkCodeCannotBecomeBlackhawk}
                helperText={
                  blackhawkCodeError ||
                  (blackhawkCodeCannotBecomeBlackhawk
                    ? 'This cannot be a Blackhawk card code. Use the LE internal gift card form above.'
                    : undefined)
                }
                inputProps={CODE_INPUT_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="PIN"
                value={blackhawkPin}
                onChange={(event) => {
                  setBlackhawkPin(event.target.value);
                  setBlackhawkPinError('');
                  setBlackhawkFormError('');
                }}
                error={!!blackhawkPinError}
                helperText={blackhawkPinError || undefined}
                inputProps={CODE_INPUT_PROPS}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Region"
                value={blackhawkRegion}
                onChange={(event) => setBlackhawkRegion(event.target.value)}
              >
                {REGION_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={isBlackhawkLoading}
                sx={{ whiteSpace: 'nowrap', minWidth: 260 }}
              >
                {isBlackhawkLoading ? 'Checking...' : 'Check Blackhawk Balance'}
              </Button>
            </Grid>
          </Grid>
        </Box>
        {blackhawkFormError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {blackhawkFormError}
          </Alert>
        )}
        {blackhawkResult && <BalanceResultPanel result={blackhawkResult} />}
      </Box>
    </Container>
  );
}

export default GiftCardBalanceContainer;
